import {useEffect, useRef, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {Button} from '@common/ui/buttons/button';
import {IconButton} from '@common/ui/buttons/icon-button';
import {ArrowBackIcon} from '@common/icons/material/ArrowBack';
import {DesktopMacIcon} from '@common/icons/material/DesktopMac';
import {FullscreenIcon} from '@common/icons/material/Fullscreen';
import {FullscreenExitIcon} from '@common/icons/material/FullscreenExit';
import {PhoneIphoneIcon} from '@common/icons/material/PhoneIphone';
import {TabletMacIcon} from '@common/icons/material/TabletMac';
import {useSettings} from '@common/core/settings/use-settings';
import './rekaweb-gallery-page.css';

interface PublicTemplate {
  id: string;
  name: string;
  category: string;
}

interface PublicTemplateResponse {
  template: PublicTemplate;
}

type PreviewMode = 'desktop' | 'tablet' | 'mobile';

export function RekawebTemplatePreviewPage() {
  const {branding} = useSettings();
  const {name = ''} = useParams();
  const [mode, setMode] = useState<PreviewMode>('desktop');
  const [fullscreen, setFullscreen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(true);
  const previewFrame = useRef<HTMLIFrameElement | null>(null);
  const previewContainer = useRef<HTMLDivElement>(null);
  const templateQuery = useQuery({
    queryKey: ['showcase-template', name],
    queryFn: () =>
      apiClient
        .get<PublicTemplateResponse>(`showcase/templates/${encodeURIComponent(name)}`)
        .then(response => response.data.template),
    retry: false,
  });

  useEffect(() => {
    const templateId = templateQuery.data?.id;
    const container = previewContainer.current;
    if (!templateId || !container) return;
    setPreviewLoading(true);
    const frame = document.createElement('iframe');
    frame.className = 'rekaweb-preview-frame';
    frame.title = `Demo template ${templateQuery.data?.name}`;
    frame.setAttribute('sandbox', 'allow-scripts');
    frame.referrerPolicy = 'no-referrer';
    frame.allow = 'fullscreen';
    frame.src = `/templates/preview/${encodeURIComponent(templateId)}`;
    frame.addEventListener('load', () => setPreviewLoading(false));
    container.appendChild(frame);
    previewFrame.current = frame;
    return () => {
      frame.remove();
      previewFrame.current = null;
    };
  }, [templateQuery.data?.id, templateQuery.data?.name]);

  useEffect(() => {
    if (previewFrame.current) {
      previewFrame.current.className = `rekaweb-preview-frame is-${mode}`;
    }
  }, [mode, templateQuery.data?.id]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.querySelector('.rekaweb-preview-page')?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      setFullscreen(false);
    }
  };

  const template = templateQuery.data;
  const requestUrl = template
    ? `/cart?template=${encodeURIComponent(template.id)}`
    : undefined;

  if (templateQuery.isError) {
    return (
      <main className="rekaweb-gallery rekaweb-empty">
        <h1>Demo tidak tersedia.</h1>
        <p>Template ini mungkin belum diterbitkan.</p>
        <Button elementType={Link} to="/" variant="outline" className="mt-20">
          Kembali ke galeri
        </Button>
      </main>
    );
  }

  return (
    <main className="rekaweb-gallery rekaweb-preview-page">
      <header className="rekaweb-preview-toolbar">
        <Link className="rekaweb-wordmark" to="/" aria-label={`Kembali ke ${branding.site_name}`}>
          <span>{branding.site_name}</span>
        </Link>
        <Link className="rekaweb-preview-back" to="/">
          <ArrowBackIcon aria-hidden="true" />
          Galeri
        </Link>
        <div className="rekaweb-preview-modes" role="group" aria-label="Saiz pratonton">
          <IconButton aria-label="Pratonton desktop" title="Desktop" aria-pressed={mode === 'desktop'} onClick={() => setMode('desktop')}>
            <DesktopMacIcon />
          </IconButton>
          <IconButton aria-label="Pratonton tablet" title="Tablet" aria-pressed={mode === 'tablet'} onClick={() => setMode('tablet')}>
            <TabletMacIcon />
          </IconButton>
          <IconButton aria-label="Pratonton telefon" title="Telefon" aria-pressed={mode === 'mobile'} onClick={() => setMode('mobile')}>
            <PhoneIphoneIcon />
          </IconButton>
        </div>
        <IconButton
          className="rekaweb-preview-fullscreen"
          aria-label={fullscreen ? 'Keluar skrin penuh' : 'Skrin penuh'}
          title={fullscreen ? 'Keluar skrin penuh' : 'Skrin penuh'}
          onClick={toggleFullscreen}
        >
          {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
        <Button
          href={requestUrl}
          variant="flat"
          color="primary"
          className="rekaweb-order-button rekaweb-preview-order"
          disabled={!template}
        >
          Budget Website (Template)
        </Button>
      </header>
      {previewLoading && <p className="px-20 py-8 text-center text-sm" role="status">Memuatkan demo...</p>}
      <div className="rekaweb-preview-frame-wrap" ref={previewContainer} />
    </main>
  );
}